import { useEffect, useState, type ReactNode } from 'react';
import { Card, DataTable, Modal, FormField, FormSelect, TextInput, Toggle, Drawer, StatusBadge, EmptyState } from '@/components/ui';
import { SectionToolbar } from './telephony/SectionToolbar';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { labelFor } from '@/shared/lib/labels';
import { adminApi, type MohSoundItem } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';

interface Queue {
  id: number;
  name: string;
  ext: string;
  strategy: string;
  sla_sec: number;
  callback_enabled?: boolean;
  settings?: QueueSettings | null;
  agents_count?: number;
}

interface QueueMember {
  id: number;
  name: string;
  role?: string;
  extension?: string | null;
  penalty?: number;
  active?: boolean;
}

interface AvailableAgent {
  id: number;
  name: string;
  role?: string;
  extension?: string | null;
}

interface QueueSettings {
  max_wait_sec?: number;
  overflow_route?: 'hangup' | 'voicemail' | 'ext' | 'queue';
  overflow_target?: string;
  use_hold_music?: boolean;
  moh_sound_id?: number | null;
  wrapup_sec?: number;
  maxlen?: number;
  ringinuse?: boolean;
}

const EMPTY = {
  name: '', ext: '', strategy: 'round-robin', sla_sec: 20, callback_enabled: false,
  settings: {
    max_wait_sec: 300, overflow_route: 'hangup', overflow_target: '',
    use_hold_music: true, wrapup_sec: 0, maxlen: 0, ringinuse: false,
  } as QueueSettings,
};

export function Queues({ tabs }: { tabs?: ReactNode } = {}) {
  const [rows, setRows] = useState<Queue[]>([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [editTarget, setEditTarget] = useState<Queue | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Queue | null>(null);
  const [drawer, setDrawer] = useState<Queue | null>(null);
  const [form, setForm] = useState({ ...EMPTY });

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    const res = await adminApi.queues();
    setRows(res.data as Queue[]);
    setLoading(false);
  }

  async function add() {
    if (!form.name || !form.ext) return;
    await adminApi.createQueue(form);
    toast('נוצר תור', 'ok');
    setShow(false);
    setForm({ ...EMPTY });
    void load();
  }

  function openEdit(q: Queue) {
    setForm({
      name: q.name, ext: q.ext, strategy: q.strategy, sla_sec: q.sla_sec,
      callback_enabled: !!q.callback_enabled,
      settings: { ...EMPTY.settings, ...(q.settings ?? {}) },
    });
    setEditTarget(q);
  }

  async function saveEdit() {
    if (!editTarget || !form.name || !form.ext) return;
    try {
      await adminApi.updateQueue(editTarget.id, form);
      toast('עודכן', 'ok');
      setEditTarget(null);
      setForm({ ...EMPTY });
      setDrawer(null);
      void load();
    } catch {
      toast('שגיאה בעדכון', 'danger');
    }
  }

  async function remove(q: Queue) {
    try {
      await adminApi.deleteQueue(q.id);
      toast('התור נמחק', 'ok');
      setDeleteTarget(null);
      setDrawer(null);
      setRows((prev) => prev.filter((r) => r.id !== q.id));
    } catch {
      toast('שגיאה במחיקה', 'danger');
    }
  }

  const cols: DataTableColumn<Queue>[] = [
    { key: 'name', he: 'שם' },
    { key: 'ext', he: 'שלוחה' },
    { key: 'strategy', he: 'אסטרטגיה', render: (r) => labelFor('strategy', r.strategy) },
    { key: 'sla_sec', he: 'SLA (שניות)' },
    {
      key: 'agents_count',
      he: 'נציגים',
      render: (r) => (r.agents_count === 0
        ? <StatusBadge tone="warn" size="sm" dot>ללא נציגים</StatusBadge>
        : <span>{r.agents_count ?? '—'}</span>),
    },
  ];

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <SectionToolbar tabs={tabs} title="תורי שירות" actions={<><button type="button" className="btn primary" onClick={() => setShow(true)}>+ הוסף תור</button></>} />
      <Card padded={false}><DataTable<Queue> columns={cols} rows={rows} loading={loading} onRowClick={setDrawer} emptyState={<EmptyState icon="info" title="אין עדיין תורי שירות" description="צור תור ראשון כדי לנתב שיחות נכנסות לנציגים." action={<button type="button" className="btn primary" onClick={() => setShow(true)}>+ הוסף תור</button>} />} /></Card>

      <Modal isOpen={show} onClose={() => { setShow(false); setForm({ ...EMPTY }); }} title="תור חדש"
        footer={<><button type="button" className="btn" onClick={() => { setShow(false); setForm({ ...EMPTY }); }}>ביטול</button><button type="button" className="btn primary" onClick={add}>שמור</button></>}>
        <QueueFields form={form} setForm={setForm} />
      </Modal>

      {editTarget && (
        <Modal isOpen onClose={() => { setEditTarget(null); setForm({ ...EMPTY }); }} title={`עריכת ${editTarget.name}`}
          footer={<><button type="button" className="btn" onClick={() => { setEditTarget(null); setForm({ ...EMPTY }); }}>ביטול</button><button type="button" className="btn primary" onClick={saveEdit}>שמור</button></>}>
          <QueueFields form={form} setForm={setForm} />
        </Modal>
      )}

      {deleteTarget && (
        <Modal isOpen onClose={() => setDeleteTarget(null)} title="מחיקת תור"
          footer={<><button type="button" className="btn" onClick={() => setDeleteTarget(null)}>ביטול</button><button type="button" className="btn danger" onClick={() => remove(deleteTarget)}>מחק</button></>}>
          <p>למחוק את התור <strong>{deleteTarget.name}</strong>? שיחות שמנותבות אליו יושפעו.</p>
        </Modal>
      )}

      <Drawer isOpen={!!drawer} onClose={() => setDrawer(null)} title={drawer?.name}>
        {drawer && (
          <div className="stack" style={{ gap: 16 }}>
            <div className="stack" style={{ gap: 4 }}>
              <div><strong>שלוחה:</strong> {drawer.ext}</div>
              <div><strong>אסטרטגיה:</strong> {labelFor('strategy', drawer.strategy)}</div>
              <div><strong>SLA:</strong> {drawer.sla_sec} שניות</div>
              <div><strong>חזרה אוטומטית:</strong> {drawer.callback_enabled ? 'פעיל' : 'כבוי'}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn" onClick={() => openEdit(drawer)}>ערוך הגדרות</button>
              <button type="button" className="btn danger" onClick={() => setDeleteTarget(drawer)}>מחק</button>
            </div>
            <QueueMembers
              queue={drawer}
              onChanged={(count) => {
                setRows((prev) => prev.map((r) => (r.id === drawer.id ? { ...r, agents_count: count } : r)));
              }}
            />
          </div>
        )}
      </Drawer>
    </div>
  );
}

function QueueFields({ form, setForm }: { form: typeof EMPTY; setForm: (f: typeof EMPTY) => void }) {
  // צלילי ספריית ה-MOH לבחירה פר-תור (ברירת מחדל = צליל ברירת-המחדל של העסק).
  const [mohSounds, setMohSounds] = useState<MohSoundItem[]>([]);
  useEffect(() => { adminApi.mohSounds().then(setMohSounds).catch(() => {}); }, []);

  return (
    <div className="stack">
      <FormField label="שם" required><TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormField>
      <FormField label="שלוחה" required><TextInput value={form.ext} onChange={(e) => setForm({ ...form, ext: e.target.value })} dir="ltr" /></FormField>
      <FormField label="אסטרטגיה">
        <FormSelect value={form.strategy} onChange={(e) => setForm({ ...form, strategy: e.target.value })}>
          <option value="round-robin">Round Robin</option>
          <option value="longest-idle">הכי פנוי</option>
          <option value="skills">לפי כישורים</option>
          <option value="ringall">צלצול לכולם</option>
        </FormSelect>
      </FormField>
      <FormField label="זמן צלצול לנציג (SLA, שניות)" hint="כמה זמן מצלצל אצל נציג בכל ניסיון לפני מעבר לנציג הבא.">
        <TextInput type="number" value={String(form.sla_sec)} onChange={(e) => setForm({ ...form, sla_sec: Number(e.target.value) })} dir="ltr" />
      </FormField>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap)' }}>
        <FormField label="המתנה מקסימלית בתור (שניות)" hint="אחרי הזמן הזה השיחה גולשת ליעד הגלישה.">
          <TextInput
            type="number" dir="ltr"
            value={String(form.settings.max_wait_sec ?? 300)}
            onChange={(e) => setForm({ ...form, settings: { ...form.settings, max_wait_sec: Number(e.target.value) } })}
          />
        </FormField>
        <FormField label="יעד גלישה" hint="לאן ממשיכה שיחה שלא נענתה בזמן.">
          <FormSelect
            value={form.settings.overflow_route ?? 'hangup'}
            onChange={(e) => setForm({ ...form, settings: { ...form.settings, overflow_route: e.target.value as QueueSettings['overflow_route'] } })}
          >
            <option value="hangup">ניתוק</option>
            <option value="voicemail">תא קולי</option>
            <option value="ext">שלוחה</option>
            <option value="queue">תור אחר</option>
          </FormSelect>
        </FormField>
      </div>
      {(form.settings.overflow_route === 'ext' || form.settings.overflow_route === 'voicemail' || form.settings.overflow_route === 'queue') && (
        <FormField label={form.settings.overflow_route === 'queue' ? 'שלוחת התור לגלישה' : 'שלוחת היעד לגלישה'} hint="מספר השלוחה (של נציג או תור).">
          <TextInput
            dir="ltr"
            value={form.settings.overflow_target ?? ''}
            onChange={(e) => setForm({ ...form, settings: { ...form.settings, overflow_target: e.target.value } })}
          />
        </FormField>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap)' }}>
        <FormField label="זמן התאוששות לנציג (שניות)" hint="Wrap-up: כמה זמן הנציג לא מקבל שיחה נוספת מהתור אחרי סיום שיחה.">
          <TextInput
            type="number" dir="ltr"
            value={String(form.settings.wrapup_sec ?? 0)}
            onChange={(e) => setForm({ ...form, settings: { ...form.settings, wrapup_sec: Number(e.target.value) } })}
          />
        </FormField>
        <FormField label="מקסימום ממתינים" hint="0 = ללא הגבלה.">
          <TextInput
            type="number" dir="ltr"
            value={String(form.settings.maxlen ?? 0)}
            onChange={(e) => setForm({ ...form, settings: { ...form.settings, maxlen: Number(e.target.value) } })}
          />
        </FormField>
      </div>

      <Toggle
        checked={!!form.settings.use_hold_music}
        onChange={(v) => setForm({ ...form, settings: { ...form.settings, use_hold_music: v } })}
        label="השמע מוזיקת המתנה בתור"
      />
      {form.settings.use_hold_music && (
        <FormField label="צליל המתנה לתור זה" hint={mohSounds.length === 0 ? 'אין צלילים בספרייה — העלה בהגדרות ⇐ חברה ⇐ ספריית מוזיקת המתנה.' : 'בחר צליל מהספרייה, או השאר על ברירת המחדל של העסק.'}>
          <FormSelect
            value={form.settings.moh_sound_id ? String(form.settings.moh_sound_id) : ''}
            onChange={(e) => setForm({ ...form, settings: { ...form.settings, moh_sound_id: e.target.value ? Number(e.target.value) : null } })}
          >
            <option value="">ברירת המחדל של העסק</option>
            {mohSounds.map((s) => <option key={s.id} value={s.id}>{s.name}{s.is_default ? ' (ברירת מחדל)' : ''}</option>)}
          </FormSelect>
        </FormField>
      )}
      <Toggle
        checked={!!form.settings.ringinuse}
        onChange={(v) => setForm({ ...form, settings: { ...form.settings, ringinuse: v } })}
        label="צלצל גם לנציג שכבר בשיחה"
      />
      <Toggle checked={form.callback_enabled} onChange={(v) => setForm({ ...form, callback_enabled: v })} label="אפשר חזרה אוטומטית" />
    </div>
  );
}

function QueueMembers({ queue, onChanged }: { queue: Queue; onChanged: (count: number) => void }) {
  const [members, setMembers] = useState<QueueMember[]>([]);
  const [available, setAvailable] = useState<AvailableAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addId, setAddId] = useState<number | ''>('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    void adminApi.queueMembers(queue.id).then((d) => {
      if (!alive) return;
      setMembers((d.members as QueueMember[]) ?? []);
      setAvailable((d.available as AvailableAgent[]) ?? []);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [queue.id]);

  async function persist(next: QueueMember[]) {
    setSaving(true);
    try {
      const res = await adminApi.syncQueueMembers(
        queue.id,
        next.map((m) => ({ id: m.id, penalty: m.penalty ?? 0, active: m.active ?? true })),
      );
      const data = res as { members: QueueMember[]; available: AvailableAgent[] } | undefined;
      if (data) {
        setMembers(data.members ?? next);
        setAvailable(data.available ?? available);
        onChanged((data.members ?? next).length);
      } else {
        onChanged(next.length);
      }
      toast('רשימת הנציגים עודכנה', 'ok');
    } catch {
      toast('שגיאה בעדכון הנציגים', 'danger');
    } finally {
      setSaving(false);
    }
  }

  function addAgent() {
    if (!addId) return;
    const agent = available.find((a) => a.id === addId);
    if (!agent) return;
    const next = [...members, { ...agent, penalty: 0, active: true }];
    setAddId('');
    void persist(next);
  }

  function removeAgent(id: number) {
    void persist(members.filter((m) => m.id !== id));
  }

  function setPenalty(id: number, penalty: number) {
    setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, penalty } : m)));
  }

  function toggleActive(id: number) {
    const next = members.map((m) => (m.id === id ? { ...m, active: !m.active } : m));
    void persist(next);
  }

  return (
    <Card title={`נציגי התור (${members.length})`} padded>
      {loading ? (
        <p style={{ color: 'var(--muted)' }}>טוען…</p>
      ) : (
        <div className="stack" style={{ gap: 10 }}>
          {members.length === 0 ? (
            <StatusBadge tone="warn" size="sm" dot>אין נציגים — שיחות לא ייענו</StatusBadge>
          ) : (
            <div className="stack" style={{ gap: 6 }}>
              {members.map((m) => (
                <div
                  key={m.id}
                  style={{
                    display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: 8, alignItems: 'center',
                    padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600 }}>{m.name}</div>
                    {m.extension && <div dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>שלוחה {m.extension}</div>}
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                    עדיפות
                    <input
                      type="number" min={0} max={999} value={m.penalty ?? 0}
                      onChange={(e) => setPenalty(m.id, Number(e.target.value))}
                      onBlur={() => void persist(members)}
                      style={{ width: 56, padding: '4px 6px', border: '1px solid var(--line)', borderRadius: 'var(--r-1)', background: 'var(--surface)' }}
                      dir="ltr"
                    />
                  </label>
                  <button
                    type="button" className={`btn ghost`} style={{ padding: '4px 8px', fontSize: 'var(--t-xs)' }}
                    onClick={() => toggleActive(m.id)} disabled={saving}
                  >
                    {m.active === false ? 'מושבת' : 'פעיל'}
                  </button>
                  <button type="button" className="btn danger" style={{ padding: '4px 8px', fontSize: 'var(--t-xs)' }} onClick={() => removeAgent(m.id)} disabled={saving}>
                    הסר
                  </button>
                </div>
              ))}
            </div>
          )}

          {available.length > 0 && (
            <div style={{ display: 'flex', gap: 8 }}>
              <FormSelect value={addId} onChange={(e) => setAddId(e.target.value ? Number(e.target.value) : '')} style={{ flex: 1 }}>
                <option value="">הוסף נציג…</option>
                {available.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}{a.extension ? ` (${a.extension})` : ''}</option>
                ))}
              </FormSelect>
              <button type="button" className="btn primary" onClick={addAgent} disabled={!addId || saving}>הוסף</button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
